import * as THREE from 'three';

export default class AudioManager {
    constructor(camera, scene) {
        this.camera = camera;
        this.scene = scene;
        this.audioContext = null;
        this.audioListener = null;
        this.audioLoader = null;
        
        // 3D 音频源数组
        this.audioSources = new Map();
        
        // 环境音乐（非 3D）
        this.ambientAudio = null;
        this.ambientMusic = null;

        this.isPaused = false;
        
        this.initAudio();
    }

    initAudio() {
        try {
            // 创建 Three.js AudioListener
            this.audioListener = new THREE.AudioListener();
            
            // 将 AudioListener 添加到相机
            if (this.camera) {
                this.camera.add(this.audioListener);
            }
            
            // 创建 AudioLoader
            this.audioLoader = new THREE.AudioLoader();
            
            
        } catch (error) {
            console.error('Failed to initialize 3D Audio:', error);
        }
    }

    // 加载并创建环境音乐（非定位音频）
    async loadAmbientMusic(url) {
        return new Promise((resolve, reject) => {
            if (!this.audioListener) {
                reject(new Error('AudioListener not initialized'));
                return;
            }

            this.audioLoader.load(
                url,
                (buffer) => {
                    try {
                        // 创建白噪音
                        this.ambientAudio = new THREE.Audio(this.audioListener);
                        this.ambientAudio.setBuffer(buffer);
                        this.ambientAudio.setLoop(true);
                        this.ambientAudio.setVolume(1);
                        
                        resolve(true);
                    } catch (error) {
                        console.error('Failed to create ambient audio:', error);
                        reject(error);
                    }
                },
                (progress) => {
                    
                },
                (error) => {
                    console.error('Failed to load ambient music:', error);
                    reject(error);
                }
            );
        });
    }

    async loadAmbientMusicWithBGM(url) {
        return new Promise((resolve, reject) => {
            if (!this.audioListener) {
                reject(new Error('AudioListener not initialized'));
                return;
            }

            this.audioLoader.load(
                url,
                (buffer) => {
                    try {
                        // 创建环境音乐
                        this.ambientMusic = new THREE.Audio(this.audioListener);
                        this.ambientMusic.setBuffer(buffer);
                        this.ambientMusic.setLoop(true);
                        this.ambientMusic.setVolume(0.5);

                        resolve(true);
                    } catch (error) {
                        console.error('Failed to create ambient audio with BGM:', error);
                        reject(error);
                    }
                },
                (progress) => {
                    
                },
                (error) => {
                    console.error('Failed to load ambient music with BGM:', error);
                    reject(error);
                }
            );
        });
    }

    // 创建 3D 定位音频源
    async createPositionalAudio(id, url, position, options = {}) {
        return new Promise((resolve, reject) => {
            if (!this.audioListener) {
                reject(new Error('AudioListener not initialized'));
                return;
            }

            this.audioLoader.load(
                url,
                (buffer) => {
                    try {
                        // 创建定位音频
                        const positionalAudio = new THREE.PositionalAudio(this.audioListener);
                        positionalAudio.setBuffer(buffer);
                        positionalAudio.setRefDistance(options.refDistance || 5);
                        positionalAudio.setRolloffFactor(options.rolloffFactor || 1);
                        positionalAudio.setDistanceModel(options.distanceModel || 'inverse');
                        positionalAudio.setMaxDistance(options.maxDistance || 100);
                        positionalAudio.setLoop(options.loop || false);
                        positionalAudio.setVolume(options.volume || 0.5);

                        // 创建音频源对象
                        const audioObject = new THREE.Object3D();
                        audioObject.position.copy(position);
                        audioObject.add(positionalAudio);
                        
                        // 添加到场景
                        this.scene.add(audioObject);
                        
                        // 保存引用
                        this.audioSources.set(id, {
                            audio: positionalAudio,
                            object: audioObject,
                            options: options
                        });
                        

                        resolve(positionalAudio);
                    } catch (error) {
                        console.error(`Failed to create positional audio '${id}':`, error);
                        reject(error);
                    }
                },
                (progress) => {
                    
                },
                (error) => {
                    console.error(`Failed to load audio '${id}':`, error);
                    reject(error);
                }
            );
        });
    }

    // 播放环境音乐
    async playAmbientMusic() {
        if (this.ambientAudio && !this.ambientAudio.isPlaying) {
            try {
                this.ambientAudio.play();
                
                setTimeout(() => {
                    if (this.ambientMusic && !this.ambientMusic.isPlaying) {
                        this.ambientMusic.setLoop(true);
                        this.ambientMusic.setVolume(0.3);
                        this.ambientMusic.play();
                    }
                }, 10000); // 延迟3秒播放bgm

            } catch (error) {
                console.error('Failed to play ambient music:', error);
            }
        }
    }

    // 停止环境音乐
    stopAmbientMusic() {
        if (this.ambientAudio && this.ambientAudio.isPlaying) {
            this.ambientAudio.stop();

        }
    }

    // 播放定位音频
    playPositionalAudio(id) {
        const audioSource = this.audioSources.get(id);
        if (audioSource && !audioSource.audio.isPlaying) {
            audioSource.audio.play();
            
        }
    }

    // 停止定位音频
    stopPositionalAudio(id) {
        const audioSource = this.audioSources.get(id);
        if (audioSource && audioSource.audio.isPlaying) {
            audioSource.audio.stop();

        }
    }

    stopAmbientMusic(){
        if (this.ambientAudio ) {
            this.ambientAudio.stop();
        }
        
        if (this.ambientMusic ) {
            this.ambientMusic.stop();
        }

        this.isPaused = true;
    }
    resumeAmbientMusic() {
        if (this.ambientAudio ) {
            this.ambientAudio.play();
        }
        
        if (this.ambientMusic) {
            this.ambientMusic.play();
        }
        this.isPaused = false;
    }

    // 设置环境音乐音量
    setAmbientVolume(volume) {
        if (this.ambientAudio) {
            this.ambientAudio.setVolume(Math.max(0, Math.min(1, volume)));
        }
    }

    // 设置定位音频音量
    setPositionalVolume(id, volume) {
        const audioSource = this.audioSources.get(id);
        if (audioSource) {
            audioSource.audio.setVolume(Math.max(0, Math.min(1, volume)));
        }
    }

    // 更新音频源位置
    updateAudioPosition(id, position) {
        const audioSource = this.audioSources.get(id);
        if (audioSource) {
            audioSource.object.position.copy(position);
        }
    }

    // 暂停所有音频
    pauseAll() {
        if (this.ambientAudio && this.ambientAudio.isPlaying) {
            this.ambientAudio.pause();
        }
        
        this.audioSources.forEach((source, id) => {
            if (source.audio.isPlaying) {
                source.audio.pause();
            }
        });

        this.isPaused = true;
    }

    // 恢复所有音频
    resumeAll() {
        if (this.ambientAudio && !this.ambientAudio.isPlaying) {
            this.ambientAudio.play();
        }
        
        this.audioSources.forEach((source, id) => {
            if (!source.audio.isPlaying && source.options.autoResume !== false) {
                source.audio.play();
            }
        });

        this.isPaused = false;
    }

    // 清理资源
    dispose() {
        // 停止所有音频
        this.stopAmbientMusic();
        this.audioSources.forEach((source, id) => {
            this.stopPositionalAudio(id);
            this.scene.remove(source.object);
        });
        
        this.audioSources.clear();
        
        if (this.audioListener && this.camera) {
            this.camera.remove(this.audioListener);
        }
    }
}